/**
 * Units to add for an addon checkbox. Reads `data-quantity` (set from the
 * addon block's Quantity setting) and falls back to 1 for older markup.
 * @param {HTMLInputElement} checkbox
 */
function addonQuantity(checkbox) {
  const parsed = Number.parseInt(checkbox.dataset.quantity || '1', 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
}

const APEX_STAND_VARIANT_MAP = {
  1500: {
    floor: 42001223122979,
    motorized: 42001223254051,
    universal: 42001223188515,
  },
  2160: {
    floor: 42001223155747,
    motorized: 42001223286819,
    universal: 42001223221283,
  },
}

const APEX_STAND_PRICE_MAP = {
  floor: 11900,
  motorized: 79900,
  universal: 34900,
}

/** Shopify cents per stand type (`floor`/`motorized`/`universal`). Omit a key → compare-at uses selling price. */
const APEX_STAND_COMPARE_AT_MAP = {
  floor: 15900,
  universal: 39900,
  motorized: 89900,
}

const APEX_PANEL_PRICE_MAP = {
  1500: {
    '1p': { price: 99900, compareAt: 129900, label: '$999/ea' },
    '2p': { price: 89900, compareAt: 129900, label: '$899/ea' },
    '4p': { price: 84900, compareAt: 129900, label: '$849/ea' },
  },
  2160: {
    '1p': { price: 159900, compareAt: 219900, label: '$1,599/ea' },
    '2p': { price: 149900, compareAt: 219900, label: '$1,499/ea' },
    '4p': { price: 139900, compareAt: 219900, label: '$1,399/ea' },
  },
}

const APEX_STAND_TITLE_MAP = {
  floor: 'Floor Stand',
  motorized: 'Motorized Stand',
  universal: 'Universal Stand',
}

const APEX_WARRANTIES = {
  1500: 42222856896547,
  2160: 42222856994851,
}

/** Query param keys for Apex PDP state; values match `data-apex-input` radio `value`s. */
const APEX_URL_PARAM_VARIANT = 'variant'
const APEX_URL_PARAM_QUANTITY = 'quantity'
const APEX_URL_PARAM_STAND = 'stand'

const APEX_DEFAULT_QUANTITY = '1p'
const APEX_DEFAULT_STAND = 'bare'

/** @param {string} src @param {number} width */
function sizeShopifyImageUrl(src, width) {
  if (!src) return src
  try {
    const url = new URL(src, window.location.href)
    if (!url.hostname.endsWith('shopify.com')) return src
    url.searchParams.set('width', String(width))
    return url.toString()
  } catch (_) {
    return src
  }
}

class BodyPanelProductForm extends HTMLElement {
  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true
      this.applyApexQueryParamsToForm()
      this.syncApexWarrantyAddonRows()
      this.setupAddonPreventUncheck()
      this.setupPriceUpdating()
    }
    // Always (re)bind: disconnectedCallback removes the document-level click
    // delegate, and setupAddToCart is idempotent (cached handler ref + addEventListener dedupe).
    this.setupAddToCart()
    this.addEventListener('elite-stock-mode-changed', () => this.syncEliteStockMode())
  }

  setupAddonPreventUncheck() {
    /**
     * Capture-phase click: stops default where supported (e.g. pointer / desktop).
     * Touch UAs often toggle the checkbox during activation before click can be cancelled reliably.
     */
    const interceptUncheckIntent = (event) => {
      if (!(event.target instanceof Element)) return
      if (event.target.closest('button, a[href]')) return
      /** @type {HTMLInputElement | null} */
      let input = null
      if (event.target instanceof HTMLInputElement && event.target.name === 'addon-product') {
        input = event.target
      } else {
        const label = event.target.closest('label')
        const control =
          label && typeof HTMLLabelElement !== 'undefined' && label instanceof HTMLLabelElement ? label.control : null
        if (control instanceof HTMLInputElement && control.name === 'addon-product') input = control
      }
      if (!input || input.type !== 'checkbox') return
      if (!input.hasAttribute('data-prevent-uncheck')) return
      if (!input.checked) return
      event.preventDefault()
    }

    /** After toggle: restores checked state when uncheck slipped through on touch / mixed event order. */
    const revertDisallowedUncheck = (event) => {
      const input = event.target
      if (!(input instanceof HTMLInputElement) || input.name !== 'addon-product') return
      if (!input.hasAttribute('data-prevent-uncheck')) return
      if (input.checked) return
      const apexRow = input.closest('[data-apex-warranty]')
      if (apexRow instanceof HTMLElement && apexRow.classList.contains('hidden')) return
      input.checked = true
    }

    this.addEventListener('click', interceptUncheckIntent, true)
    this.addEventListener('change', revertDisallowedUncheck)
  }

  setupAddToCart() {
    // Use document-level delegation so sticky nav buttons are also handled.
    this.onDocumentAddToCartClick =
      this.onDocumentAddToCartClick ||
      ((e) => {
        if (!(e.target instanceof Element)) return
        const $button = e.target.closest('[data-add-to-cart]')
        if (!$button || $button.disabled) return
        const addToCartPayload = this.buildAddToCartPayload()
        if (!addToCartPayload.items.length) return
        $button.dataset.loading = ''
        this.executeAddToCart(addToCartPayload, $button)
      })
    document.addEventListener('click', this.onDocumentAddToCartClick)
  }

  disconnectedCallback() {
    if (this.onDocumentAddToCartClick) {
      document.removeEventListener('click', this.onDocumentAddToCartClick)
    }
  }

  readAutomaticDiscountByVariantId() {
    const discountsData = this.querySelector('#variant-automatic-discounts')
    if (!discountsData) return {}
    try {
      const discounts = JSON.parse(discountsData.textContent || '{}')
      return discounts && typeof discounts === 'object' ? discounts : {}
    } catch (error) {
      console.error('[body-panel-product-form] Error parsing variant automatic discounts:', error)
      return {}
    }
  }

  buildAddToCartPayload() {
    const addToCartPayload = { items: [] }
    const apexSelection = this.getApexSelection()

    addToCartPayload.items.unshift({
      quantity: apexSelection.quantity,
      id: apexSelection.variantId,
    })

    if (apexSelection.standVariantId) {
      addToCartPayload.items.push({
        quantity: 1,
        id: apexSelection.standVariantId,
        properties: { _addon: true },
      })
    }

    this.getSelectedAddons().forEach((addon) => {
      addToCartPayload.items.push(addon)
    })

    this.getAutoAddons().forEach((addon) => {
      addToCartPayload.items.unshift(addon)
    })

    return addToCartPayload
  }

  getApexSelection() {
    const $model = this.querySelector('[data-apex-input="model"]:checked')
    const $quantity = this.querySelector('[data-apex-input="quantity"]:checked')
    const $stand = this.querySelector('[data-apex-input="stand"]:checked')
    const model = $model?.value || '1500'
    const standValue = $stand?.value ?? null
    const quantityValue = $quantity?.value || APEX_DEFAULT_QUANTITY
    const stand = standValue || APEX_DEFAULT_STAND
    const variantId = Number($model?.dataset.apexVariantId || this.getAttribute('variant-id'))
    const quantity = Number.parseInt(quantityValue, 10) || 1
    const standVariantId = APEX_STAND_VARIANT_MAP[model]?.[stand] || null

    return { model, quantity, quantityValue, stand, standValue, variantId, standVariantId }
  }

  getAutoAddons() {
    try {
      const autoAddons = JSON.parse(this.getAttribute('auto-addons') || '[]')
      return Array.isArray(autoAddons) ? autoAddons : []
    } catch (error) {
      console.error('Error parsing autoAddons:', error)
      return []
    }
  }

  /**
   * Matches cart payload logic: unchecked/empty skips; Apex warranty rows only count when visible for model.
   * @param {Element} node
   * @returns {node is HTMLInputElement}
   */
  isEligibleAddonCheckbox(node) {
    if (!(node instanceof HTMLInputElement) || node.name !== 'addon-product') return false
    if (!node.checked || node.value === '') return false
    const { model } = this.getApexSelection()
    const warrantyRow = node.closest('[data-apex-warranty]')
    if (!warrantyRow) return true
    const forModel = warrantyRow.getAttribute('data-apex-warranty-for')
    const expectedId = APEX_WARRANTIES[model]
    return forModel === model && expectedId != null && Number(node.value) === expectedId
  }

  /** @param {HTMLInputElement} checkbox */
  getAddonSellingAndCompareAtCents(checkbox) {
    const sell = Number.parseInt(checkbox.dataset.price || '0', 10) || 0
    const compareRaw = checkbox.dataset.compareAtPrice
    let compareAt = sell
    if (compareRaw != null && compareRaw !== '') {
      compareAt = Number.parseInt(String(compareRaw), 10) || sell
    }
    return { sell, compareAt }
  }

  /**
   * @param {string} stand
   * @returns {{ sellCents: number, compareAtCents: number }}
   */
  getStandSellingAndCompareAtCents(stand) {
    if (!stand || stand === 'bare') return { sellCents: 0, compareAtCents: 0 }
    const sellCents = APEX_STAND_PRICE_MAP[stand] || 0
    const mapped = APEX_STAND_COMPARE_AT_MAP[stand]
    const compareAtCents = mapped == null ? sellCents : mapped
    return { sellCents, compareAtCents }
  }

  getSelectedAddons() {
    return Array.from(this.querySelectorAll('[name="addon-product"]:checked'))
      .filter(($addon) => this.isEligibleAddonCheckbox($addon))
      .map(($addon) => ({
        quantity: addonQuantity($addon),
        id: Number($addon.value),
        properties: { _addon: true },
      }))
  }

  /**
   * Only the warranty that matches the selected panel model (1500 / 2160) is shown;
   * the other is hidden and unchecked so it is not in the add-to-cart payload.
   * The newly visible warranty copies the previous panel’s on/off state so a checked
   * warranty stays included when switching models.
   */
  syncApexWarrantyAddonRows() {
    const { model } = this.getApexSelection()
    const $prevWarrantyInput = this.querySelector('[data-apex-warranty]:not(.hidden) [name="addon-product"]')
    const carryWarrantyChecked = $prevWarrantyInput != null ? $prevWarrantyInput.checked : true

    this.querySelectorAll('[data-apex-warranty][data-apex-warranty-for]').forEach((wrapper) => {
      const forModel = wrapper.getAttribute('data-apex-warranty-for')
      const $input = wrapper.querySelector('input[name="addon-product"]')
      if (forModel === model) {
        wrapper.classList.remove('hidden')
        if ($input instanceof HTMLInputElement) {
          const locked = $input.hasAttribute('data-prevent-uncheck')
          $input.checked = locked ? true : carryWarrantyChecked
        }
      } else {
        wrapper.classList.add('hidden')
        if ($input instanceof HTMLInputElement) $input.checked = false
      }
    })
    ;[
      '[data-gift-1-value-for]',
      '[data-gift-original-price-for]',
      '[data-gift-savings-text-for]',
      '[data-apex-total-value-for]',
    ].forEach((sel) => {
      this.querySelectorAll(sel).forEach((span) => {
        const attr = sel.slice(1, -1)
        if (span.getAttribute(attr) === model) {
          span.classList.remove('hidden')
        } else {
          span.classList.add('hidden')
        }
      })
    })
  }

  syncEliteStockMode() {
    const mode = this.getAttribute('data-elite-stock-mode') || 'default'
    const { model } = this.getApexSelection()
    const isElite = model === '2160'
    const atcBtn = this.querySelector('[data-add-to-cart]')
    const atcText = atcBtn?.querySelector('[data-text]')
    const badgeWrap = this.querySelector('[data-apex-availability-badge]')
    const eliteBadge = this.querySelector('[data-apex-elite-badge]')

    if (this.hasAttribute('data-force-out-of-stock')) {
      if (atcBtn) {
        atcBtn.disabled = true
        atcBtn.classList.add('bg-silver', '!cursor-not-allowed')
      }
      if (atcText) atcText.textContent = 'SOLD OUT'
      return
    }

    if (eliteBadge) {
      eliteBadge.classList.toggle('hidden', mode === 'default')
    }

    if (mode === 'default' || !isElite) {
      if (atcBtn) {
        atcBtn.disabled = false
        atcBtn.classList.remove('bg-silver', '!cursor-not-allowed')
      }
      if (atcText) atcText.textContent = 'Add to Cart'
      if (badgeWrap) {
        const dot = badgeWrap.querySelector('.rounded-full')
        const spans = badgeWrap.querySelectorAll('.inline-flex.font-semibold > span')
        if (dot) dot.style.backgroundColor = '#F9CE61'
        if (spans[0]) spans[0].textContent = 'Limited Stock'
        if (spans[2]) spans[2].textContent = 'Ships in 1-2 Days'
      }
      return
    }

    if (mode === 'pre-order') {
      if (atcBtn) {
        atcBtn.disabled = false
        atcBtn.classList.remove('bg-silver', '!cursor-not-allowed')
      }
      if (atcText) atcText.textContent = 'Pre-Order'
    } else if (mode === 'out-of-stock') {
      if (atcBtn) {
        atcBtn.disabled = true
        atcBtn.classList.add('bg-silver', '!cursor-not-allowed')
      }
      if (atcText) atcText.textContent = 'Sold Out'
    }

    if (badgeWrap) {
      const dot = badgeWrap.querySelector('.rounded-full')
      const spans = badgeWrap.querySelectorAll('.inline-flex.font-semibold > span')
      if (dot) dot.style.backgroundColor = '#EF4444'
      if (spans[0]) spans[0].textContent = mode === 'pre-order' ? 'Pre-Order' : 'SOLD OUT'
      if (spans[2]) spans[2].textContent = mode === 'pre-order' ? 'Ships by 7/18' : 'AVAILABLE ON 7/18'
    }
  }

  executeAddToCart(addToCartPayload, $addToCartButton) {
    // Shopify applies lines in API order; reversing matches desired cart display (main product first, etc.).
    const payload = { items: [...addToCartPayload.items].reverse() }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const error = await response.json()
          return alert(error.description || error.message)
        }

        window.NSHybridCart.open()
      })
      .catch((response) => {
        console.error('Error adding to cart:', response)
      })
      .finally(() => {
        delete $addToCartButton.dataset.loading
      })
  }

  setupPriceUpdating() {
    if (this._apexPriceUpdatingBound) return
    const $productVariantsData = this.querySelector('#product-variants-data')
    if (!$productVariantsData) return

    this._apexPriceUpdatingBound = true
    const productVariants = JSON.parse($productVariantsData.innerHTML)
    const automaticDiscountByVariantId = this.readAutomaticDiscountByVariantId()
    const getAutomaticDiscountCents = (variant) => {
      if (variant) {
        const fromMap = Number(automaticDiscountByVariantId[String(variant.id)])
        if (Number.isFinite(fromMap) && fromMap >= 0) return fromMap
      }
      return Number(this.getAttribute('automatic-discount')) || 0
    }
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      trailingZeroDisplay: 'stripIfInteger',
    })

    const flushPriceUpdate = () => {
      const apexSelection = this.getApexSelection()
      const { model, quantity, variantId, stand } = apexSelection
      const modelChanged = this._lastApexFlushedModel !== model

      // Warranty rows / per-qty labels / elite badge only depend on model.
      if (modelChanged) {
        this.syncApexWarrantyAddonRows()
        this.updateQuantityPriceLabels(model)
      }

      const addonKey = Array.from(this.querySelectorAll('[name="addon-product"]:checked'))
        .filter(($addon) => this.isEligibleAddonCheckbox($addon))
        .map(($addon) => $addon.value)
        .join(',')
      const signature = `${model}|${apexSelection.quantityValue}|${stand}|${variantId}|${addonKey}`
      if (this._lastApexPriceSignature === signature) return

      const variant = productVariants.find((v) => Number(v.id) === variantId) || productVariants[0]
      if (!variant) return

      const variantPrice = Number(variant.price) || 0
      const variantCompareAt = Number(variant.compare_at_price || variant.price) || 0
      const quantityKey = `${quantity}p`
      const panelPricing = APEX_PANEL_PRICE_MAP[model]?.[quantityKey]
      const automaticDiscount = getAutomaticDiscountCents(variant)
      const panelSellPerUnit = panelPricing?.price ?? Math.max(variantPrice - automaticDiscount, 0)
      const panelComparePerUnit = panelPricing?.compareAt ?? variantCompareAt
      const panelsSellCents = panelSellPerUnit * quantity
      const panelsCompareAtCents = panelComparePerUnit * quantity

      const { sellCents: standSellCents, compareAtCents: standCompareAtCents } =
        this.getStandSellingAndCompareAtCents(stand)

      let addonsSellCents = 0
      let addonsCompareAtCents = 0
      this.querySelectorAll('[name="addon-product"]:checked').forEach(($addon) => {
        if (!$addon || !this.isEligibleAddonCheckbox($addon)) return
        if ($addon.dataset.giftId) return
        const { sell, compareAt } = this.getAddonSellingAndCompareAtCents($addon)
        addonsSellCents += sell
        addonsCompareAtCents += compareAt
      })

      const totalSellCents = panelsSellCents + standSellCents + addonsSellCents
      const totalCompareAtCents = panelsCompareAtCents + standCompareAtCents + addonsCompareAtCents

      this.updatePriceText(totalSellCents, totalCompareAtCents, formatter)
      this.updateAffirm(totalSellCents)
      this.syncApexUrlState()
      this.emitApexSelectionUpdate({
        apexSelection,
        variant,
        panelSellPerUnit,
        panelComparePerUnit,
        standSellCents,
        standCompareAtCents,
        formatter,
      })
      if (modelChanged) this.syncEliteStockMode()
      this._lastApexFlushedModel = model
      this._lastApexPriceSignature = signature
    }

    // Yield one frame so stand availability / accordion can paint before Affirm,
    // URL, and the gallery rebuild that `apex-selection-update` triggers.
    const schedulePriceUpdate = () => {
      if (this._apexPriceUpdateRaf) return
      this._apexPriceUpdateRaf = requestAnimationFrame(() => {
        this._apexPriceUpdateRaf = 0
        flushPriceUpdate()
      })
    }

    this.addEventListener('change', (event) => {
      const target = event.target
      if (!(target instanceof HTMLInputElement)) return
      if (!target.hasAttribute('data-apex-input') && target.name !== 'addon-product') return
      schedulePriceUpdate()
    })

    flushPriceUpdate()
  }

  emitApexSelectionUpdate({
    apexSelection,
    variant,
    panelSellPerUnit,
    panelComparePerUnit,
    standSellCents,
    standCompareAtCents,
    formatter,
  }) {
    const detail = {
      selection: {
        model: apexSelection.model,
        quantity: apexSelection.quantity,
        quantityValue: apexSelection.quantityValue,
        stand: apexSelection.stand,
        variantId: apexSelection.variantId,
        standVariantId: apexSelection.standVariantId,
      },
      items: [
        this.buildApexPanelSelectionItem({
          apexSelection,
          variant,
          panelSellPerUnit,
          panelComparePerUnit,
          formatter,
        }),
      ],
      selectedAddons: this.getSelectedAddonSelectionItems(formatter),
    }

    const standItem = this.buildApexStandSelectionItem({
      apexSelection,
      standSellCents,
      standCompareAtCents,
      formatter,
    })
    if (standItem) detail.items.push(standItem)

    detail.items.push(...detail.selectedAddons)

    window.apexSelectionUpdateDetail = detail
    window.dispatchEvent(new CustomEvent('apex-selection-update', { detail }))
  }

  buildApexPanelSelectionItem({ apexSelection, variant, panelSellPerUnit, panelComparePerUnit, formatter }) {
    const panelQuantity = apexSelection.quantity
    const linePriceCents = panelSellPerUnit * panelQuantity
    const lineCompareAtCents = panelComparePerUnit * panelQuantity
    const title = this.getApexPanelTitle(apexSelection.model, variant)
    const modelInput = this.querySelector(`[data-apex-input="model"][value="${apexSelection.model}"]`)

    return {
      type: 'panel',
      title,
      variantId: apexSelection.variantId,
      quantity: panelQuantity,
      image: this.getVariantImageUrl(variant) || modelInput?.dataset.imageUrl || '',
      imageAlt: title,
      url: variant?.url || window.location.pathname,
      unitPriceCents: panelSellPerUnit,
      unitCompareAtPriceCents: panelComparePerUnit,
      linePriceCents,
      lineCompareAtPriceCents: lineCompareAtCents,
      price: this.formatMoney(linePriceCents, formatter),
      compareAtPrice: this.formatMoney(lineCompareAtCents, formatter),
    }
  }

  buildApexStandSelectionItem({ apexSelection, standSellCents, standCompareAtCents, formatter }) {
    if (!apexSelection.standVariantId || standSellCents <= 0) return null
    const standInput = this.querySelector('[data-apex-input="stand"]:checked')
    const label = this.getInputLabel(standInput)
    const title = APEX_STAND_TITLE_MAP[apexSelection.stand] || label?.textContent?.trim() || 'Stand'
    const image = this.getSelectedOptionImage(label, apexSelection.model)

    return {
      type: 'stand',
      title,
      variantId: apexSelection.standVariantId,
      quantity: 1,
      image,
      imageAlt: title,
      url: '',
      unitPriceCents: standSellCents,
      unitCompareAtPriceCents: standCompareAtCents,
      linePriceCents: standSellCents,
      lineCompareAtPriceCents: standCompareAtCents,
      price: this.formatMoney(standSellCents, formatter),
      compareAtPrice: this.formatMoney(standCompareAtCents, formatter),
      properties: { _addon: true },
    }
  }

  getSelectedAddonSelectionItems(formatter) {
    return Array.from(this.querySelectorAll('[name="addon-product"]:checked'))
      .filter(($addon) => this.isEligibleAddonCheckbox($addon))
      .map(($addon) => {
        const { sell, compareAt } = this.getAddonSellingAndCompareAtCents($addon)
        const title = $addon.dataset.productTitle || this.getInputLabel($addon)?.textContent?.trim() || 'Add-on'
        return {
          type: 'addon',
          title,
          variantId: Number($addon.value),
          quantity: 1,
          image: $addon.dataset.imageUrl || this.getSelectedOptionImage(this.getInputLabel($addon), ''),
          imageAlt: $addon.dataset.imageAlt || title,
          url: $addon.dataset.productUrl || '',
          unitPriceCents: sell,
          unitCompareAtPriceCents: compareAt,
          linePriceCents: sell,
          lineCompareAtPriceCents: compareAt,
          price: this.formatMoney(sell, formatter),
          compareAtPrice: this.formatMoney(compareAt, formatter),
          properties: { _addon: true },
        }
      })
  }

  getApexPanelTitle(model, variant) {
    const modelInput = this.querySelector(`[data-apex-input="model"][value="${model}"]`)
    if (modelInput?.dataset.productTitle) return modelInput.dataset.productTitle
    if (variant?.title && variant.title !== 'Default Title') return variant.title
    return 'Apex Half Body Panel'
  }

  getVariantImageUrl(variant) {
    const image = variant?.featured_image || variant?.image || variant?.featured_media?.preview_image
    const src = typeof image === 'string' ? image : image?.src || image?.url || ''
    // Variant JSON exposes the unsized original; consumers (Complete Your Routine,
    // sticky nav) render it around 75px and re-fetch it on every selection change.
    return sizeShopifyImageUrl(src, 300)
  }

  getInputLabel(input) {
    if (!(input instanceof HTMLInputElement) || !input.id) return null
    return this.querySelector(`label[for="${CSS.escape(input.id)}"]`)
  }

  getSelectedOptionImage(label, model) {
    if (!label) return ''
    const modelImage = model ? label.querySelector(`img[data-apex-option-content-image="${CSS.escape(model)}"]`) : null
    const image =
      modelImage || label.querySelector('img:not([data-apex-option-content-image])') || label.querySelector('img')
    return image?.getAttribute('src') || ''
  }

  formatMoney(cents, formatter) {
    return formatter.format((Number(cents) || 0) / 100)
  }

  updateQuantityPriceLabels(model) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      trailingZeroDisplay: 'stripIfInteger',
    })
    this.querySelectorAll('[data-apex-input="quantity"]').forEach(($quantityInput) => {
      const label = this.querySelector(`label[for="${$quantityInput.id}"]`)
      const priceEl = label?.querySelector('[data-apex-quantity-price]')
      const compareEl = label?.querySelector('[data-apex-quantity-compare-price]')
      const panelPricing = APEX_PANEL_PRICE_MAP[model]?.[$quantityInput.value]
      if (priceEl && panelPricing) priceEl.textContent = panelPricing.label
      if (compareEl) {
        const singlePanelPrice = APEX_PANEL_PRICE_MAP[model]?.['1p']?.price
        const isMultiPanel = $quantityInput.value !== '1p'
        if (isMultiPanel && singlePanelPrice) {
          compareEl.textContent = `${formatter.format(singlePanelPrice / 100)}/ea`
          compareEl.classList.remove('hidden')
        } else {
          compareEl.classList.add('hidden')
        }
      }
    })
  }

  /**
   * @param {number} totalSellCents panels + stand + addons (eligible), after discounts
   * @param {number} totalCompareAtCents stacked compare-at across those same pieces
   */
  updatePriceText(totalSellCents, totalCompareAtCents, formatter) {
    // NOTE: scoped to `document` (not `this`) so cloned/co-rendered pricing markup
    // outside the <body-panel-product-form> custom element — e.g. pdp-sticky-nav's
    // apex-body-compare-line — is kept in sync on variant changes. Safe because
    // this controller only mounts on the apex body PDP.
    const formattedPrice = formatter.format(totalSellCents / 100)
    document.querySelectorAll('[data-main-product-price]').forEach(($price) => {
      $price.textContent = formattedPrice
    })

    const hasCompareAt = totalCompareAtCents > totalSellCents
    const formattedCompareAtPrice = formatter.format(totalCompareAtCents / 100)
    document.querySelectorAll('[data-main-product-compare-at-price]').forEach(($compareAtPrice) => {
      $compareAtPrice.textContent = `(Total Value ${formattedCompareAtPrice})`
    })

    document.querySelectorAll('[data-apex-compare-line]').forEach((row) => {
      if (row instanceof HTMLElement) row.classList.toggle('hidden', !hasCompareAt)
    })

    document.querySelectorAll('[data-apex-save-pill]').forEach((el) => {
      if (el instanceof HTMLElement) el.classList.toggle('hidden', !hasCompareAt)
    })

    document.querySelectorAll('[data-promo-text]').forEach(($promoText) => {
      if ($promoText.dataset.autoDiscountPromo === 'true') return
      const originalText = $promoText.dataset.promoOriginalText || $promoText.textContent
      const formattedSavings = formatter.format(Math.max(0, totalCompareAtCents - totalSellCents) / 100)
      $promoText.textContent = originalText.replace(/\$[\d,]+(?:\.\d{2})?/, formattedSavings)
    })
  }

  updateAffirm(totalPrice) {
    const affirmEl = this.querySelector('[data-body-affirm-ala]')
    if (!affirmEl) return
    affirmEl.setAttribute('data-amount', String(Math.round(totalPrice)))
    if (this._affirmRefreshTimer) clearTimeout(this._affirmRefreshTimer)
    this._affirmRefreshTimer = setTimeout(() => {
      this._affirmRefreshTimer = 0
      if (typeof window.affirm === 'object' && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') {
        try {
          window.affirm.ui.refresh()
        } catch (_) {}
      }
    }, 200)
  }

  /**
   * Applies `?variant=`, `?quantity=`, `?stand=` (e.g. 1p, bare) from the current URL to radios on load.
   */
  applyApexQueryParamsToForm() {
    if (typeof window === 'undefined' || !window.location?.search) return
    try {
      const sp = new URLSearchParams(window.location.search)
      const v = sp.get(APEX_URL_PARAM_VARIANT)
      const q = sp.get(APEX_URL_PARAM_QUANTITY)
      const s = sp.get(APEX_URL_PARAM_STAND)

      if (v) {
        for (const el of this.querySelectorAll('[data-apex-input="model"]')) {
          // Disabled == locked out by a single-model template; `?variant=` must not re-enable it.
          if (
            el instanceof HTMLInputElement &&
            !el.disabled &&
            el.dataset.apexVariantId &&
            String(el.dataset.apexVariantId) === v
          ) {
            el.checked = true
            break
          }
        }
      }
      if (q) {
        for (const el of this.querySelectorAll('[data-apex-input="quantity"]')) {
          if (el instanceof HTMLInputElement && el.value === q) {
            el.checked = true
            break
          }
        }
      }
      if (s) {
        for (const el of this.querySelectorAll('[data-apex-input="stand"]')) {
          if (el instanceof HTMLInputElement && el.value === s) {
            el.checked = true
            break
          }
        }
      }
    } catch (_) {}
  }

  /**
   * Syncs hidden line-item `id`, `variant-id` on this element, and `variant` / `quantity` / `stand` query params.
   */
  syncApexUrlState() {
    const { variantId, standValue } = this.getApexSelection()
    const $quantity = this.querySelector('[data-apex-input="quantity"]:checked')
    const quantityValue = $quantity instanceof HTMLInputElement ? $quantity.value : null
    const vid = Number(variantId)
    if (!Number.isFinite(vid) || vid <= 0) return

    const $lineItemId = this.querySelector('input[name="id"]')
    if ($lineItemId) $lineItemId.value = String(vid)
    this.setAttribute('variant-id', String(vid))

    if (typeof window === 'undefined' || !window.history?.replaceState) return
    try {
      const url = new URL(window.location.href)
      const before = url.href
      url.searchParams.set(APEX_URL_PARAM_VARIANT, String(vid))
      if (quantityValue) {
        url.searchParams.set(APEX_URL_PARAM_QUANTITY, quantityValue)
      } else {
        url.searchParams.delete(APEX_URL_PARAM_QUANTITY)
      }
      if (standValue) {
        url.searchParams.set(APEX_URL_PARAM_STAND, standValue)
      } else {
        url.searchParams.delete(APEX_URL_PARAM_STAND)
      }
      if (url.href === before) return
      window.history.replaceState({}, '', url.toString())
    } catch (_) {}
  }
}

if (!customElements.get('body-panel-product-form')) {
  customElements.define('body-panel-product-form', BodyPanelProductForm)
}
